import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from "@components/ui/dialog";
import { EnumRecruiterTypes } from "@utils/constants";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Input } from "@components/ui/input";
import { PlusIcon, SearchIcon, XIcon } from "lucide-react";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import regexList from "@utils/regexList";
import { RecruiterTypes } from "@utils/constants";
import {
  getRecruiterInvitations,
  approvalRequest,
  inviteRecruiterMember,
  updateRecruiterInvitationSso,
  accountMembers,
} from "@endpoints/recruiter.service";
import dayjs from "dayjs";
import { RecruiterApprovalStatus } from "@utils/constants";
import { roleOptions } from "../sections/AccountMembers";

interface InviteAccountMemberModalProps {
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
}

type InviteFormValues = {
  firstName: string;
  lastName: string;
  company: string;
  jobTitle?: string;
  phone?: string;
  email: string;
  address1: string;
  address2?: string;
  city?: string;
  country: string;
  postCode?: string;
  role: number;
  ssoEnabled?: boolean;
};

const InviteAccountMemberModal = ({
  openModal,
  setOpenModal,
}: InviteAccountMemberModalProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const companyName = useSelector((state: any) => {
    return (
      state?.auth?.companyDetails?.name || state?.auth?.user?.companyName || ""
    );
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    watch,
  } = useForm<InviteFormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      company: "",
      jobTitle: "",
      phone: "",
      email: "",
      address1: "",
      address2: "",
      city: "",
      country: "",
      postCode: "",
      role: EnumRecruiterTypes.ADMIN,
      ssoEnabled: false,
    },
  });

  const fields = watch();
  const [isInviting, setIsInviting] = useState(false);

  useEffect(() => {
    if (companyName) {
      setValue("company", companyName);
    }
  }, [companyName, setValue]);

  const handleInvite = async (payload: InviteFormValues) => {
    try {
      setIsInviting(true);
      const { success } = await inviteRecruiterMember({
        ...payload,
        company: companyName || payload.company,
        role: Number(payload.role),
        ssoEnabled: Boolean(payload.ssoEnabled),
      });
      if (success) {
        enqueueSnackbar("Invitation sent successfully.", {
          variant: "success",
        });
        reset({
          firstName: "",
          lastName: "",
          company: "",
          jobTitle: "",
          phone: "",
          email: "",
          address1: "",
          address2: "",
          city: "",
          country: "",
          postCode: "",
          role: RecruiterTypes.ADMIN,
          ssoEnabled: false,
        });
        setOpenModal(false);
        // await fetchAccountMembers();
      }
    } catch (error) {
      enqueueSnackbar(error || "Failed to send invitation", {
        variant: "error",
      });
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <Dialog open={openModal} onOpenChange={setOpenModal}>
      <DialogContent showCloseButton={false} className="bg-white">
        <div className="flex items-center justify-between border-b">
          <p className="text-sm font-semibold">Invite Member</p>
          <button
            type="button"
            className="p-1 text-gray-500 hover:text-gray-700"
            onClick={() => setOpenModal(false)}
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-col flex-1 max-h-96 overflow-y-scroll">
          <form onSubmit={handleSubmit(handleInvite)}>
            <div className="px-6 py-5">
              <div className="grid grid-cols-2 gap-6">
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="text-xs font-medium">First name *</label>
                    <Input
                      {...register("firstName", {
                        required: "First name is required",
                      })}
                      className="mt-1 text-xs"
                    />
                    {errors.firstName && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium">Last name *</label>
                    <Input
                      {...register("lastName", {
                        required: "Last name is required",
                      })}
                      className="mt-1 text-xs"
                    />
                    {errors.lastName && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium">Company *</label>
                    <Input
                      {...register("company", {
                        required: "Company is required",
                      })}
                      className="mt-1 text-xs"
                      value={fields.company || companyName}
                      readOnly={Boolean(companyName)}
                      disabled={Boolean(companyName)}
                    />
                    {errors.company && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.company.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium">Job Title</label>
                    <Input {...register("jobTitle")} className="mt-1 text-xs" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Phone</label>
                    <Input {...register("phone")} className="mt-1 text-xs" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">
                      Email Address *
                    </label>
                    <Input
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: regexList.email,
                          message: "Invalid email address",
                        },
                      })}
                      className="mt-1 text-xs"
                    />
                    {errors.email && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="text-xs font-medium">Address 1 *</label>
                    <Input
                      {...register("address1", {
                        required: "Address 1 is required",
                      })}
                      className="mt-1 text-xs"
                    />
                    {errors.address1 && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.address1.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium">Address 2</label>
                    <Input {...register("address2")} className="mt-1 text-xs" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">City</label>
                    <Input {...register("city")} className="mt-1 text-xs" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Country *</label>
                    <Input
                      {...register("country", {
                        required: "Country is required",
                      })}
                      className="mt-1 text-xs"
                    />
                    {errors.country && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.country.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium">Post Code</label>
                    <Input {...register("postCode")} className="mt-1 text-xs" />
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <p className="text-sm font-semibold mb-2">User Role</p>
                <div className="grid grid-cols-2 gap-6 items-start">
                  <div>
                    <label className="text-xs font-medium">User Role *</label>
                    <select
                      {...register("role", {
                        required: "Role is required",
                      })}
                      className="mt-1 h-9 w-full rounded-md border border-input bg-transparent px-3 text-xs font-medium"
                    >
                      {roleOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-xs mt-6">
                    <input
                      type="checkbox"
                      checked={!!fields?.ssoEnabled}
                      onChange={(e) => setValue("ssoEnabled", e.target.checked)}
                    />
                    Enable Microsoft SSO for this member
                  </label>
                </div>
              </div>
            </div>
          </form>
        </div>
        <DialogFooter>
          <button
            type="button"
            className="rounded-md border border-gray-200 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100"
            onClick={() => setOpenModal(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex items-center justify-center rounded-md py-2 px-4 gap-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold text-xs"
            disabled={isInviting}
          >
            {isInviting ? (
              <LoadingAnimation className="w-4 h-4" color="white" />
            ) : null}
            Invite Members
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteAccountMemberModal;
